const screwsManifest = require('../../ufov/screws/generated/imagegen/manifest.json');
const eggsManifest = require('../../ufov/eggs/generated/imagegen/manifest.json');
const keysManifest = require('../../ufov/keys/generated/imagegen/manifest.json');
const facesManifest = require('../../ufov/faces/generated/imagegen/manifest.json');
const dogsManifest = require('../../ufov/dogs/generated/imagegen/manifest.json');
const pillsManifest = require('../../ufov/pills/generated/imagegen/manifest.json');
const flowersManifest = require('../../ufov/flowers/generated/imagegen/manifest.json');
const carsManifest = require('../../ufov/cars/generated/imagegen/manifest.json');
const littleCarsManifest = require('../../ufov/little-cars/generated/imagegen/manifest.json');
const planesManifest = require('../../ufov/planes/generated/imagegen/manifest.json');
const shellsManifest = require('../../ufov/shells/generated/imagegen/manifest.json');
const leavesManifest = require('../../ufov/leaves/generated/imagegen/manifest.json');
const rocksManifest = require('../../ufov/rocks/generated/imagegen/manifest.json');
const fruitManifest = require('../../ufov/fruits/manifest.json');

const FRUIT_SYMBOL_VARIANT_ID = 'fruit';
const DEFAULT_SYMBOL_VARIANT_ID = 'screws-box';

const BASE_ONBOARDING_NOISE_LAYOUT = [
    { id: 'n1', x: 21, y: 32, rotation: -13, size: 29, opacity: 0.76 },
    { id: 'n2', x: 40, y: 19, rotation: -4, size: 29, opacity: 0.75 },
    { id: 'n3', x: 59, y: 19, rotation: 6, size: 29, opacity: 0.75 },
    { id: 'n4', x: 73, y: 30, rotation: 10, size: 28, opacity: 0.76 },
    { id: 'n5', x: 82, y: 50, rotation: 7, size: 29, opacity: 0.77 },
    { id: 'n6', x: 76, y: 72, rotation: -8, size: 29, opacity: 0.75 },
    { id: 'n7', x: 58, y: 84, rotation: 12, size: 29, opacity: 0.74 },
    { id: 'n8', x: 39, y: 87, rotation: -3, size: 28, opacity: 0.74 },
    { id: 'n9', x: 22, y: 79, rotation: -10, size: 29, opacity: 0.75 },
    { id: 'n10', x: 12, y: 56, rotation: 8, size: 29, opacity: 0.76 },
];

const normalizeOnboardingNoiseLayout = (layout, size = 24) => (
    (Array.isArray(layout) ? layout : []).map((item) => ({
        ...item,
        rotation: 0,
        size,
    }))
);

const BASE_IMAGE_SCENARIO = {
    onboarding: {
        noiseLayout: normalizeOnboardingNoiseLayout(BASE_ONBOARDING_NOISE_LAYOUT, 24),
        reservePeripheralNoiseSlot: true,
    },
    board: {
        safeZone: {
            left: 17,
            right: 83,
            top: 17,
            bottom: 83,
        },
        noiseScale: 1.18,
        noiseOpacityBoost: 0.16,
        noiseOpacityMax: 0.86,
    },
    trial: {
        maxDistractorCount: 15,
        distractorField: {
            kind: 'two-circles',
            twoCircles: {
                slotCount: 16,
                innerRadius: 25,
                outerRadius: 42,
                angleOffset: -65,
            },
        },
        fallbackField: {
            kind: 'rect',
            safeZone: {
                left: 18,
                right: 82,
                top: 18,
                bottom: 82,
            },
        },
        centerExclusion: {
            absDx: 12,
            absDy: 14,
            radius: 14,
        },
        peripheralPlacement: {
            kind: 'reuse-candidate',
            ring: 'outer',
            maxAngleDelta: 180,
            reserveDistractorSlot: true,
        },
        spacing: {
            targetClearance: { start: 0, min: 0, relax: 0 },
            minDistance: { start: 0, min: 0, relax: 0 },
            laneShortGap: { start: 0, min: 0, relax: 0 },
            laneLongGap: { start: 0, min: 0, relax: 0 },
            reservedClearance: 1,
        },
        noise: {
            baseSize: 24,
            sizeVariance: 0,
            opacityBoost: 0.14,
            minOpacity: 0.58,
            maxOpacity: 0.84,
            lockMotion: true,
            lockRotation: true,
        },
    },
};

const createImageScenarioConfig = (safeZone = BASE_IMAGE_SCENARIO.board.safeZone) => ({
    onboarding: {
        ...BASE_IMAGE_SCENARIO.onboarding,
    },
    board: {
        ...BASE_IMAGE_SCENARIO.board,
        safeZone,
    },
    trial: BASE_IMAGE_SCENARIO.trial,
});

const buildScenarioRuntimePaths = (scenarioId, manifest) => {
    const version = Math.max(1, Number(manifest?.version) || 1);

    return {
        sheetPath: manifest?.runtime?.sheetPath || `/assets/ufov/scenarios/${scenarioId}/sheet.png?v=${version}`,
        sheetPath2x: manifest?.runtime?.sheetPath2x || `/assets/ufov/scenarios/${scenarioId}/sheet@2x.png?v=${version}`,
        boardPath: manifest?.runtime?.boardPath || `/assets/ufov/scenarios/${scenarioId}/board.png?v=${version}`,
        boardPath2x: manifest?.runtime?.boardPath2x || `/assets/ufov/scenarios/${scenarioId}/board@2x.png?v=${version}`,
    };
};

const SYMBOL_ASSET_VERSION = 1;

const buildScenarioSymbolAssetPaths = (scenarioId, symbolId, version) => ({
    symbolAssetPath: `/assets/ufov/scenarios/${scenarioId}/symbols/${symbolId}.png?v=${version}.${SYMBOL_ASSET_VERSION}`,
});

const SHEET_SCENARIO_CATEGORY_GROUPS = {
    'screws-box': {
        steel: ['screw-01', 'screw-02', 'screw-03', 'screw-04', 'screw-06', 'screw-10', 'screw-12', 'screw-14', 'screw-15'],
        brass: ['screw-07', 'screw-13', 'screw-16'],
        dark: ['screw-05', 'screw-08', 'screw-09', 'screw-11'],
    },
    'egg-nest': {
        warmNeutral: ['egg-01', 'egg-02', 'egg-06', 'egg-10', 'egg-15', 'egg-17', 'egg-19', 'egg-20'],
        blueGreenDark: ['egg-03', 'egg-05', 'egg-07', 'egg-08', 'egg-12', 'egg-13', 'egg-18'],
        pinkPurpleMixed: ['egg-04', 'egg-09', 'egg-11', 'egg-14', 'egg-16'],
    },
    'graduation-orla': {
        light: ['face-04', 'face-07', 'face-08', 'face-12', 'face-16', 'face-19'],
        medium: ['face-01', 'face-05', 'face-09', 'face-13', 'face-17', 'face-20'],
        dark: ['face-02', 'face-03', 'face-06', 'face-10', 'face-11', 'face-14', 'face-15', 'face-18'],
    },
    'key-hook-board': {
        warmMetal: ['key-02', 'key-04', 'key-07', 'key-09', 'key-12', 'key-13', 'key-14', 'key-15', 'key-16', 'key-17', 'key-19'],
        silver: ['key-01', 'key-03', 'key-08', 'key-10', 'key-11', 'key-20'],
        charcoal: ['key-05', 'key-06', 'key-18'],
    },
    'shell-tray': {
        redPink: ['shell-02', 'shell-05'],
        orangeBrown: ['shell-03', 'shell-06', 'shell-08', 'shell-10'],
        creamNeutral: ['shell-01', 'shell-04', 'shell-07', 'shell-09'],
    },
    'leaf-basket': {
        orangeRed: ['leaf-02', 'leaf-13', 'leaf-19'],
        yellowGreen: ['leaf-04', 'leaf-05', 'leaf-12', 'leaf-18'],
        deepGreen: ['leaf-01', 'leaf-03', 'leaf-06', 'leaf-07', 'leaf-08', 'leaf-09', 'leaf-10', 'leaf-11', 'leaf-14', 'leaf-15', 'leaf-16', 'leaf-17', 'leaf-20'],
    },
    'rock-carrier-box': {
        warm: ['rock-02', 'rock-05', 'rock-08', 'rock-09', 'rock-12', 'rock-13', 'rock-17', 'rock-18', 'rock-21', 'rock-22', 'rock-23'],
        coolDark: ['rock-01', 'rock-03', 'rock-07', 'rock-10', 'rock-14', 'rock-15', 'rock-16', 'rock-19', 'rock-24'],
        colorAccent: ['rock-04', 'rock-06', 'rock-11', 'rock-20'],
    },
    'dog-breed-board': {
        lightGolden: ['dog-03', 'dog-07', 'dog-11', 'dog-12', 'dog-14', 'dog-19'],
        mediumBrown: ['dog-01', 'dog-02', 'dog-04', 'dog-06', 'dog-08', 'dog-09', 'dog-15'],
        darkBrown: ['dog-05', 'dog-10', 'dog-13', 'dog-16', 'dog-17', 'dog-18', 'dog-20'],
    },
    'pillbox-board': {
        warmLight: ['pill-01', 'pill-04', 'pill-08', 'pill-10', 'pill-16'],
        neutralLight: ['pill-02', 'pill-05', 'pill-06', 'pill-11', 'pill-12', 'pill-14', 'pill-15'],
        coolBlue: ['pill-03', 'pill-07', 'pill-09', 'pill-13'],
    },
    'flower-garden': {
        yellowCream: ['flower-01', 'flower-06', 'flower-08', 'flower-13'],
        redPink: ['flower-02', 'flower-04', 'flower-05', 'flower-07', 'flower-09', 'flower-10', 'flower-11', 'flower-12', 'flower-15'],
        purple: ['flower-03', 'flower-14', 'flower-16'],
    },
    'parking-garage': {
        warm: ['car-01', 'car-03', 'car-04', 'car-09'],
        coolBlue: ['car-02', 'car-06', 'car-10', 'car-13', 'car-14', 'car-15', 'car-16'],
        greenNeutral: ['car-05', 'car-07', 'car-08', 'car-11', 'car-12'],
    },
    'little-cars': {
        warm: ['little-car-01', 'little-car-03', 'little-car-06', 'little-car-07', 'little-car-10'],
        cool: ['little-car-04', 'little-car-08', 'little-car-09'],
        green: ['little-car-02', 'little-car-05'],
    },
    'planes-sky': {
        coolBlueGray: ['plane-01', 'plane-05', 'plane-06', 'plane-07', 'plane-09', 'plane-12'],
        warmRed: ['plane-02', 'plane-03', 'plane-10', 'plane-14'],
        lightNeutral: ['plane-04', 'plane-08', 'plane-11', 'plane-13', 'plane-15', 'plane-16'],
    },
};

const DISABLED_SHEET_SYMBOL_IDS = {
    'rock-carrier-box': ['rock-19', 'rock-20', 'rock-21', 'rock-22', 'rock-23', 'rock-24'],
    'parking-garage': ['car-13'],
};

const createSheetCategoryLookup = (packId, categoryGroups) => {
    const lookup = new Map();

    Object.entries(categoryGroups || {}).forEach(([categoryId, symbolIds]) => {
        if (!Array.isArray(symbolIds)) {
            return;
        }

        symbolIds.forEach((symbolId) => {
            lookup.set(symbolId, `${packId}-${categoryId}`);
        });
    });

    return lookup;
};

const getSheetRowCategoryId = (packId, manifest, sourceCell, symbolIndex) => {
    const columns = Math.max(1, Math.round(Number(manifest.layout?.columns) || 1));
    const rows = Math.max(1, Math.round(Number(manifest.layout?.rows) || 1));
    const cellHeight = Number(manifest.layout?.cellHeight) || ((Number(manifest.layout?.sheetHeight) || rows) / rows);
    const cellTop = Number(sourceCell?.[1]);
    const rawRowIndex = Number.isFinite(cellTop) && cellHeight > 0
        ? Math.floor(cellTop / cellHeight)
        : Math.floor(symbolIndex / columns);
    const rowIndex = Math.max(0, Math.min(rows - 1, rawRowIndex));

    return `${packId}-row-${rowIndex + 1}`;
};

const SHEET_IMAGE_SCENARIO_PACKS = [
    {
        id: 'screws-box',
        label: 'Screws Box',
        manifest: screwsManifest,
        ...buildScenarioRuntimePaths('screws-box', screwsManifest),
        scenario: createImageScenarioConfig({
            left: 16,
            right: 84,
            top: 16,
            bottom: 84,
        }),
        warmupLabel: 'Preparing screws box...',
        onboardingExample: {
            centerAnswer: 'screw-03',
            centerOptions: ['screw-03', 'screw-05', 'screw-07', 'screw-08', 'screw-10', 'screw-11', 'screw-14', 'screw-16'],
            peripheralSymbolId: 'screw-11',
            noiseSymbolId: 'screw-01',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'egg-nest',
        label: 'Egg Nest',
        manifest: eggsManifest,
        ...buildScenarioRuntimePaths('egg-nest', eggsManifest),
        scenario: createImageScenarioConfig({
            left: 13,
            right: 87,
            top: 13,
            bottom: 87,
        }),
        warmupLabel: 'Preparing egg nest...',
        onboardingExample: {
            centerAnswer: 'egg-04',
            centerOptions: ['egg-04', 'egg-02', 'egg-06', 'egg-08', 'egg-10', 'egg-12', 'egg-16', 'egg-20'],
            peripheralSymbolId: 'egg-16',
            noiseSymbolId: 'egg-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'graduation-orla',
        label: 'Graduation Orla',
        manifest: facesManifest,
        ...buildScenarioRuntimePaths('graduation-orla', facesManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing graduation portraits...',
        onboardingExample: {
            centerAnswer: 'face-04',
            centerOptions: ['face-04', 'face-01', 'face-03', 'face-06', 'face-09', 'face-12', 'face-17', 'face-20'],
            peripheralSymbolId: 'face-15',
            noiseSymbolId: 'face-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'key-hook-board',
        label: 'Key Hook Board',
        manifest: keysManifest,
        ...buildScenarioRuntimePaths('key-hook-board', keysManifest),
        scenario: createImageScenarioConfig({
            left: 14,
            right: 86,
            top: 14,
            bottom: 86,
        }),
        warmupLabel: 'Preparing key board...',
        onboardingExample: {
            centerAnswer: 'key-03',
            centerOptions: ['key-03', 'key-01', 'key-04', 'key-06', 'key-09', 'key-12', 'key-15', 'key-18'],
            peripheralSymbolId: 'key-14',
            noiseSymbolId: 'key-01',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'shell-tray',
        label: 'Shell Tray',
        manifest: shellsManifest,
        ...buildScenarioRuntimePaths('shell-tray', shellsManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing shell tray...',
        onboardingExample: {
            centerAnswer: 'shell-04',
            centerOptions: ['shell-04', 'shell-01', 'shell-02', 'shell-03', 'shell-05', 'shell-06', 'shell-08', 'shell-10'],
            peripheralSymbolId: 'shell-08',
            noiseSymbolId: 'shell-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'leaf-basket',
        label: 'Leaf Basket',
        manifest: leavesManifest,
        ...buildScenarioRuntimePaths('leaf-basket', leavesManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing leaf basket...',
        onboardingExample: {
            centerAnswer: 'leaf-04',
            centerOptions: ['leaf-04', 'leaf-01', 'leaf-02', 'leaf-06', 'leaf-09', 'leaf-12', 'leaf-15', 'leaf-20'],
            peripheralSymbolId: 'leaf-11',
            noiseSymbolId: 'leaf-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'rock-carrier-box',
        label: 'Rock Carrier Box',
        manifest: rocksManifest,
        ...buildScenarioRuntimePaths('rock-carrier-box', rocksManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing rock carrier...',
        onboardingExample: {
            centerAnswer: 'rock-04',
            centerOptions: ['rock-04', 'rock-01', 'rock-02', 'rock-06', 'rock-09', 'rock-12', 'rock-18', 'rock-24'],
            peripheralSymbolId: 'rock-11',
            noiseSymbolId: 'rock-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'dog-breed-board',
        label: 'Dog Breed Board',
        manifest: dogsManifest,
        ...buildScenarioRuntimePaths('dog-breed-board', dogsManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 13,
            bottom: 87,
        }),
        warmupLabel: 'Preparing dog breeds...',
        onboardingExample: {
            centerAnswer: 'dog-04',
            centerOptions: ['dog-04', 'dog-01', 'dog-02', 'dog-06', 'dog-09', 'dog-12', 'dog-15', 'dog-20'],
            peripheralSymbolId: 'dog-16',
            noiseSymbolId: 'dog-08',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'pillbox-board',
        label: 'Pillbox Board',
        manifest: pillsManifest,
        ...buildScenarioRuntimePaths('pillbox-board', pillsManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing pillbox...',
        onboardingExample: {
            centerAnswer: 'pill-04',
            centerOptions: ['pill-04', 'pill-01', 'pill-02', 'pill-06', 'pill-09', 'pill-12', 'pill-14', 'pill-16'],
            peripheralSymbolId: 'pill-11',
            noiseSymbolId: 'pill-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'flower-garden',
        label: 'Flower Garden',
        manifest: flowersManifest,
        ...buildScenarioRuntimePaths('flower-garden', flowersManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing flower garden...',
        onboardingExample: {
            centerAnswer: 'flower-04',
            centerOptions: ['flower-04', 'flower-01', 'flower-02', 'flower-06', 'flower-09', 'flower-12', 'flower-14', 'flower-16'],
            peripheralSymbolId: 'flower-11',
            noiseSymbolId: 'flower-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'parking-garage',
        label: 'Parking Garage',
        manifest: carsManifest,
        ...buildScenarioRuntimePaths('parking-garage', carsManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing parking garage...',
        onboardingExample: {
            centerAnswer: 'car-04',
            centerOptions: ['car-04', 'car-01', 'car-02', 'car-06', 'car-09', 'car-12', 'car-14', 'car-16'],
            peripheralSymbolId: 'car-11',
            noiseSymbolId: 'car-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'little-cars',
        label: 'Little Cars',
        manifest: littleCarsManifest,
        ...buildScenarioRuntimePaths('little-cars', littleCarsManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing little cars...',
        onboardingExample: {
            centerAnswer: 'little-car-04',
            centerOptions: ['little-car-04', 'little-car-01', 'little-car-02', 'little-car-03', 'little-car-06', 'little-car-07', 'little-car-08', 'little-car-10'],
            peripheralSymbolId: 'little-car-09',
            noiseSymbolId: 'little-car-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
    {
        id: 'planes-sky',
        label: 'Planes Sky',
        manifest: planesManifest,
        ...buildScenarioRuntimePaths('planes-sky', planesManifest),
        scenario: createImageScenarioConfig({
            left: 12,
            right: 88,
            top: 12,
            bottom: 88,
        }),
        warmupLabel: 'Preparing sky board...',
        onboardingExample: {
            centerAnswer: 'plane-04',
            centerOptions: ['plane-04', 'plane-01', 'plane-02', 'plane-06', 'plane-09', 'plane-12', 'plane-14', 'plane-16'],
            peripheralSymbolId: 'plane-11',
            noiseSymbolId: 'plane-02',
            peripheralPoint: { x: 78, y: 31 },
        },
    },
];

const toDisplayName = (rawId) => (
    `${rawId || ''}`
        .split('-')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
);

const buildCategoryState = (symbols) => {
    const orderedCategories = [];
    const categoryMap = new Map();
    const categoryBySymbol = new Map();

    symbols.forEach((symbol) => {
        if (!categoryMap.has(symbol.categoryId)) {
            const nextCategory = {
                id: symbol.categoryId,
                symbols: [],
            };

            categoryMap.set(symbol.categoryId, nextCategory);
            orderedCategories.push(nextCategory);
        }

        const category = categoryMap.get(symbol.categoryId);
        category.symbols.push(symbol.id);
        categoryBySymbol.set(symbol.id, symbol.categoryId);
    });

    return {
        categories: orderedCategories.map((category) => ({
            ...category,
            symbols: [...category.symbols],
        })),
        categoryMap: new Map(
            orderedCategories.map((category) => [category.id, [...category.symbols]])
        ),
        categoryBySymbol,
    };
};

const createSymbolMap = (symbols) => new Map(symbols.map((symbol) => [symbol.id, symbol]));

const createOnboardingExample = (fallbackSymbolId, symbols, onboardingExample) => {
    const symbolIds = symbols.map((symbol) => symbol.id);

    if (onboardingExample) {
        const available = new Set(symbolIds);
        const safeFallback = available.has(fallbackSymbolId)
            ? fallbackSymbolId
            : symbolIds[0] || fallbackSymbolId;
        const centerAnswer = available.has(onboardingExample.centerAnswer)
            ? onboardingExample.centerAnswer
            : safeFallback;
        const centerOptions = Array.isArray(onboardingExample.centerOptions)
            ? onboardingExample.centerOptions.filter((symbolId) => available.has(symbolId))
            : [];
        const filledCenterOptions = [
            centerAnswer,
            ...centerOptions.filter((symbolId) => symbolId !== centerAnswer),
            ...symbolIds.filter((symbolId) => symbolId !== centerAnswer && !centerOptions.includes(symbolId)),
        ].slice(0, 8);

        return {
            ...onboardingExample,
            centerAnswer,
            centerOptions: filledCenterOptions,
            peripheralSymbolId: available.has(onboardingExample.peripheralSymbolId)
                ? onboardingExample.peripheralSymbolId
                : symbolIds.find((symbolId) => symbolId !== centerAnswer) || centerAnswer,
            noiseSymbolId: available.has(onboardingExample.noiseSymbolId)
                ? onboardingExample.noiseSymbolId
                : symbolIds.find((symbolId) => symbolId !== centerAnswer) || centerAnswer,
        };
    }

    return {
        centerAnswer: fallbackSymbolId,
        centerOptions: symbolIds.slice(0, 8),
        peripheralSymbolId: symbolIds[0] || fallbackSymbolId,
        noiseSymbolId: symbolIds[1] || symbolIds[0] || fallbackSymbolId,
        peripheralPoint: { x: 78, y: 31 },
    };
};

const createImagePreloadAsset = (path, preloadKey) => (
    path
        ? {
            path,
            preloadKey: preloadKey || path,
            as: 'image',
            type: 'image/png',
            fetchPriority: 'high',
        }
        : null
);

const dedupePreloadAssets = (assets) => {
    const seenPaths = new Set();

    return assets
        .filter((asset) => asset?.path)
        .filter((asset) => {
            if (seenPaths.has(asset.path)) {
                return false;
            }

            seenPaths.add(asset.path);
            return true;
        });
};

const expandVariantPreloadAssets = (preloadAssets, board, atlas) => dedupePreloadAssets([
    ...(Array.isArray(preloadAssets) ? preloadAssets : []),
    createImagePreloadAsset(board?.path, board?.preloadKey || 'board'),
    createImagePreloadAsset(board?.path2x, `${board?.preloadKey || 'board'}@2x`),
    ...(atlas?.preload === false
        ? []
        : [
            createImagePreloadAsset(atlas?.path, atlas?.preloadKey || 'atlas'),
            createImagePreloadAsset(atlas?.path2x, `${atlas?.preloadKey || 'atlas'}@2x`),
        ]),
]);

const createSymbolVariant = ({
    id,
    label,
    rootClassName,
    symbols,
    fallbackSymbolId,
    onboardingExample,
    preloadAssets = [],
    board = null,
    atlas = null,
    scenario,
    warmupLabel = '',
}) => {
    const categoryState = buildCategoryState(symbols);

    return {
        id,
        label,
        rootClassName,
        symbols: symbols.map((symbol) => ({ ...symbol })),
        symbolMap: createSymbolMap(symbols),
        pool: symbols.map((symbol) => symbol.id),
        categories: categoryState.categories,
        categoryMap: categoryState.categoryMap,
        categoryBySymbol: categoryState.categoryBySymbol,
        fallbackSymbolId,
        onboardingExample,
        preloadAssets: expandVariantPreloadAssets(preloadAssets, board, atlas),
        board,
        atlas,
        scenario,
        warmupLabel,
        maxOptionCount: symbols.length,
    };
};

const createImagePackVariant = ({
    id,
    label,
    manifest,
    rootClassName,
    scenario,
    warmupLabel,
}) => {
    const symbols = Array.isArray(manifest.symbols) ? manifest.symbols : [];
    const fallbackSymbolId = manifest.fallbackSymbolId || symbols[0]?.id || '';

    return {
        ...createSymbolVariant({
            id,
            label,
            rootClassName,
            symbols,
            fallbackSymbolId,
            onboardingExample: createOnboardingExample(
                fallbackSymbolId,
                symbols,
                manifest.onboardingExample
            ),
            preloadAssets: Array.isArray(manifest.preloadAssets) ? manifest.preloadAssets : [],
            board: manifest.board || null,
            atlas: manifest.atlas || null,
            scenario,
            warmupLabel,
        }),
        maxOptionCount: Number(manifest.maxOptionCount) || symbols.length,
    };
};

const createSheetPackVariant = ({
    id,
    label,
    manifest,
    boardPath,
    boardPath2x,
    sheetPath,
    sheetPath2x,
    scenario,
    warmupLabel,
    onboardingExample,
    categoryGroups,
}) => {
    const atlasWidth = Number(manifest.layout?.sheetWidth) || 1;
    const atlasHeight = Number(manifest.layout?.sheetHeight) || 1;
    const version = Math.max(1, Number(manifest?.version) || 1);
    const preloadKeySheet = `${id}-sheet`;
    const preloadKeyBoard = `${id}-board`;
    const categoryLookup = createSheetCategoryLookup(id, categoryGroups || SHEET_SCENARIO_CATEGORY_GROUPS[id]);
    const disabled = new Set(DISABLED_SHEET_SYMBOL_IDS[id] || []);
    const symbols = (Array.isArray(manifest.symbols) ? manifest.symbols : [])
        .filter((symbol) => !disabled.has(symbol.id))
        .map((symbol, symbolIndex) => {
        const sourceCell = Array.isArray(symbol.sourceCell) ? symbol.sourceCell.map(Number) : [0, 0, atlasWidth, atlasHeight];
        const [left, top, right, bottom] = sourceCell;

        return {
            id: symbol.id,
            displayName: symbol.displayName || toDisplayName(symbol.id),
            categoryId: symbol.categoryId || categoryLookup.get(symbol.id) || getSheetRowCategoryId(id, manifest, sourceCell, symbolIndex),
            assetPath: sheetPath,
            ...buildScenarioSymbolAssetPaths(id, symbol.id, version),
            preloadKey: preloadKeySheet,
            fallbackSymbolId: manifest.fallbackSymbolId || manifest.symbols?.[0]?.id || '',
            sprite: {
                x: left,
                y: top,
                width: Math.max(1, right - left),
                height: Math.max(1, bottom - top),
            },
        };
    });

    return createImagePackVariant({
        id,
        label,
        manifest: {
            fallbackSymbolId: manifest.fallbackSymbolId || symbols[0]?.id || '',
            maxOptionCount: Number(manifest.maxOptionCount) || symbols.length,
            onboardingExample,
            preloadAssets: [
                {
                    path: boardPath,
                    preloadKey: preloadKeyBoard,
                    as: 'image',
                    type: 'image/png',
                    fetchPriority: 'high',
                },
            ],
            board: {
                path: boardPath,
                path2x: boardPath2x,
                preloadKey: preloadKeyBoard,
            },
            atlas: {
                path: sheetPath,
                path2x: sheetPath2x,
                preload: false,
                width: atlasWidth,
                height: atlasHeight,
                spriteWidth: Number(manifest.layout?.cellWidth) || 1,
                spriteHeight: Number(manifest.layout?.cellHeight) || 1,
                columns: Number(manifest.layout?.columns) || 1,
                rows: Number(manifest.layout?.rows) || 1,
                preloadKey: preloadKeySheet,
            },
            symbols,
        },
        rootClassName: 'is-variant-fruit',
        scenario,
        warmupLabel,
    });
};

const SHEET_SCENARIO_VARIANTS = SHEET_IMAGE_SCENARIO_PACKS.reduce((variants, pack) => ({
    ...variants,
    [pack.id]: createSheetPackVariant(pack),
}), {});

const FRUIT_VARIANT = createImagePackVariant({
    id: 'fruit-crate',
    label: 'Fruit Crate',
    manifest: fruitManifest,
    rootClassName: 'is-variant-fruit',
    scenario: createImageScenarioConfig({
        left: 17,
        right: 83,
        top: 17,
        bottom: 83,
    }),
    warmupLabel: 'Preparing fruit crate...',
});

const SYMBOL_VARIANTS = {
    [FRUIT_SYMBOL_VARIANT_ID]: FRUIT_VARIANT,
    'fruit-crate': FRUIT_VARIANT,
    ...SHEET_SCENARIO_VARIANTS,
};

const variantWarmups = new Map();
const assetWarmups = new Map();

const normalizeSymbolVariantId = (variantId) => {
    const normalizedId = `${variantId || ''}`.trim().toLowerCase();

    if (!normalizedId) {
        return FRUIT_SYMBOL_VARIANT_ID;
    }

    if (normalizedId === FRUIT_SYMBOL_VARIANT_ID) {
        return FRUIT_SYMBOL_VARIANT_ID;
    }

    return SYMBOL_VARIANTS[normalizedId] ? normalizedId : FRUIT_SYMBOL_VARIANT_ID;
};

const getDefaultUFOVSymbolVariant = () => (
    SYMBOL_VARIANTS[DEFAULT_SYMBOL_VARIANT_ID]
    || Object.values(SYMBOL_VARIANTS)[0]
);

const getUFOVSymbolVariant = (variantId) => (
    SYMBOL_VARIANTS[normalizeSymbolVariantId(variantId)] || getDefaultUFOVSymbolVariant()
);

const getUFOVScenarioSymbolVariantId = (variantId, scenarioId = '') => {
    const normalizedVariantId = normalizeSymbolVariantId(variantId);
    const normalizedScenarioId = `${scenarioId || ''}`.trim().toLowerCase();

    if (normalizedVariantId === FRUIT_SYMBOL_VARIANT_ID) {
        return SYMBOL_VARIANTS[normalizedScenarioId] ? normalizedScenarioId : DEFAULT_SYMBOL_VARIANT_ID;
    }

    return normalizedVariantId;
};

const getUFOVSymbolRecord = (variantId, symbolId) => {
    const variant = getUFOVSymbolVariant(variantId);

    if (variant.symbolMap.has(symbolId)) {
        return variant.symbolMap.get(symbolId);
    }

    if (variant.fallbackSymbolId && variant.symbolMap.has(variant.fallbackSymbolId)) {
        return variant.symbolMap.get(variant.fallbackSymbolId);
    }

    return null;
};

const getUFOVSymbolDisplayName = (variantId, symbolId) => {
    const record = getUFOVSymbolRecord(variantId, symbolId);

    if (!record) {
        return 'Symbol';
    }

    return record.displayName || record.id || 'Symbol';
};

const preloadVariantAsset = async (asset) => {
    if (typeof window === 'undefined' || !asset?.path) {
        return Promise.resolve();
    }

    const isImageAsset = `${asset.type || ''}`.startsWith('image/');

    if (isImageAsset && typeof window.Image === 'function') {
        return new Promise((resolve, reject) => {
            const image = new window.Image();
            let settled = false;
            const decodeLoadedImage = () => (
                typeof image.decode === 'function'
                    ? image.decode().catch(() => undefined)
                    : Promise.resolve()
            );
            const finish = () => {
                if (settled) {
                    return;
                }

                settled = true;
                decodeLoadedImage().then(resolve);
            };

            image.decoding = 'async';
            if (asset.fetchPriority) {
                image.fetchPriority = asset.fetchPriority;
            }

            image.onload = finish;
            image.onerror = () => {
                if (!settled) {
                    settled = true;
                    reject(new Error(`Failed to preload ${asset.path}`));
                }
            };
            image.src = asset.path;

            if (image.complete && image.naturalWidth > 0) {
                finish();
            }
        });
    }

    if (typeof window.fetch !== 'function') {
        return Promise.resolve();
    }

    return window.fetch(asset.path, {
        cache: 'force-cache',
        credentials: 'same-origin',
    }).then((response) => {
        if (!response.ok) {
            throw new Error(`Failed to preload ${asset.path}`);
        }

        return response.blob();
    }).then(() => undefined);
};

const preloadAssetOnce = (asset) => {
    if (!asset?.path) {
        return Promise.resolve();
    }

    if (!assetWarmups.has(asset.path)) {
        const warmupPromise = preloadVariantAsset(asset)
            .catch((error) => {
                assetWarmups.delete(asset.path);
                throw error;
            });

        assetWarmups.set(asset.path, warmupPromise);
    }

    return assetWarmups.get(asset.path);
};

const preloadUFOVSymbolVariantAssets = (variantId) => {
    const variant = getUFOVSymbolVariant(variantId);

    if (!variant.preloadAssets.length || typeof window === 'undefined') {
        return Promise.resolve(variant);
    }

    if (!variantWarmups.has(variant.id)) {
        const warmupPromise = Promise.all(variant.preloadAssets.map(preloadAssetOnce))
            .then(() => variant)
            .catch((error) => {
                variantWarmups.delete(variant.id);
                throw error;
            });

        variantWarmups.set(variant.id, warmupPromise);
    }

    return variantWarmups.get(variant.id);
};

const getSymbolPreloadAssets = (variantId, symbolId) => {
    const variant = getUFOVSymbolVariant(variantId);
    const record = getUFOVSymbolRecord(variant.id, symbolId);

    if (!record) {
        return [];
    }

    const primaryPath = record.symbolAssetPath || record.assetPath;

    return dedupePreloadAssets([
        createImagePreloadAsset(primaryPath, record.preloadKey || record.id),
    ]);
};

const preloadUFOVSymbolAssets = (variantId, symbolIds = []) => {
    const variant = getUFOVSymbolVariant(variantId);
    const uniqueSymbolIds = [...new Set((Array.isArray(symbolIds) ? symbolIds : []).filter(Boolean))];
    const symbolAssets = uniqueSymbolIds.flatMap((symbolId) => getSymbolPreloadAssets(variant.id, symbolId));
    const assets = dedupePreloadAssets([
        ...variant.preloadAssets,
        ...symbolAssets,
    ]);

    if (!assets.length || typeof window === 'undefined') {
        return Promise.resolve(variant);
    }

    return Promise.all(assets.map(preloadAssetOnce)).then(() => variant);
};

module.exports = {
    DEFAULT_SYMBOL_VARIANT_ID,
    FRUIT_SYMBOL_VARIANT_ID,
    SYMBOL_VARIANTS,
    getUFOVScenarioSymbolVariantId,
    getUFOVSymbolDisplayName,
    getUFOVSymbolRecord,
    getUFOVSymbolVariant,
    normalizeSymbolVariantId,
    preloadUFOVSymbolAssets,
    preloadUFOVSymbolVariantAssets,
};
module.exports.default = module.exports;
